import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import EditIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Edit.js';
import TrashIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Trash.js';
import { SectionHeader } from '@diligentcorp/atlas-theme-mui-lens';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { EmptyState } from 'src/components/atlas/molecules';
import { ConfirmationBeforeRemoveModal } from 'src/components/atlas/organisms/ConfirmationBeforeRemoveModal/confirmationBeforeRemoveModal';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  GetOrganisationCustomFieldSectionsQuery,
  useGetOrganisationCustomFieldSectionsQuery,
} from 'src/graphql/generated';
import { CreateCustomFieldSectionSideSheet } from './CreateCustomFieldSectionSideSheet';
import * as styles from './CustomFields.styles';
import { useCustomFieldSectionMutations } from './hooks/useCustomFieldSectionMutations';

type CustomFieldSection =
  GetOrganisationCustomFieldSectionsQuery['getOrganisationCustomFieldSections']['sections'][number];

const COLUMNS = [
  'Order',
  'Section name',
  'Fields count',
  'Last updated',
  'Actions',
];

export const reorderSectionList = (
  list: CustomFieldSection[],
  sourceIndex: number,
  destinationIndex: number,
): CustomFieldSection[] => {
  const next = [...list];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, moved);
  return next;
};

const formatDate = (date: Date | string) => {
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

interface SectionsTabProps {
  createSheetOpen: boolean;
  onCloseCreateSheet: () => void;
}

export const SectionsTab: React.FC<SectionsTabProps> = ({
  createSheetOpen,
  onCloseCreateSheet,
}) => {
  const { data, isLoading } = useGetOrganisationCustomFieldSectionsQuery();
  const { deleteSection, reorderSections, isDeleting, isReordering } =
    useCustomFieldSectionMutations();
  const [defaultSection, setDefaultSection] =
    useState<CustomFieldSection | null>(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [sectionToEdit, setSectionToEdit] = useState<
    CustomFieldSection | undefined
  >(undefined);
  const [sectionToDelete, setSectionToDelete] = useState<
    CustomFieldSection | undefined
  >(undefined);
  const [localCreateSheetOpen, setLocalCreateSheetOpen] = useState(false);

  const sections = useMemo(
    () => data?.getOrganisationCustomFieldSections?.sections ?? [],
    [data],
  );

  const [orderedSections, setOrderedSections] = useState<CustomFieldSection[]>(
    [],
  );
  useEffect(() => {
    setOrderedSections(sections);
    const defaultSection = sections.find((section) => section.isDefault);
    setDefaultSection(defaultSection ?? null);
  }, [sections]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;

    const previous = orderedSections;
    const next = reorderSectionList(
      orderedSections,
      result.source.index,
      result.destination.index,
    );

    setOrderedSections(next);

    try {
      await reorderSections({
        orders: next.map((section, index) => ({
          id: section.id,
          displayOrder: index,
        })),
      });
    } catch {
      setOrderedSections(previous);
      handleErrorToast({ toastMessage: 'Error reordering sections' });
    }
  };

  const handleOpenCreateSheet = () => {
    setLocalCreateSheetOpen(true);
  };

  const handleCloseCreateSheet = () => {
    setLocalCreateSheetOpen(false);
    onCloseCreateSheet();
  };

  const handleEdit = (section: CustomFieldSection) => {
    setSectionToEdit(section);
    setEditSheetOpen(true);
  };

  const handleDelete = (section: CustomFieldSection) => {
    if (section.isDefault) {
      return;
    }
    setSectionToDelete(section);
  };

  const confirmDelete = async () => {
    if (!sectionToDelete) {
      return;
    }

    try {
      await deleteSection({ id: sectionToDelete.id });
      handleSuccessToast({ toastMessage: 'Section deleted successfully' });
    } catch {
      handleErrorToast({ toastMessage: 'Error deleting section' });
    } finally {
      setSectionToDelete(undefined);
    }
  };

  return (
    <>
      <SectionHeader
        title="Sections"
        subtitle="Sections group custom fields on the case page. Fields appear in the order set here, inside the section they belong to."
        buttonArray={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            data-testid="sections-tab-add-new"
            onClick={handleOpenCreateSheet}
          >
            Add section
          </Button>
        }
        slotProps={{
          root: {
            sx: { mb: '24px', mt: '16px' },
          },
        }}
      />
      <TableContainer sx={styles.tableContainer}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow sx={styles.headerRow}>
                {COLUMNS.map((column) => (
                  <TableCell key={column} sx={styles.headerCell}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <Droppable droppableId="custom-field-sections">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {!isLoading &&
                    orderedSections.map((section, index) => (
                      <Draggable
                        key={section.id}
                        draggableId={section.id}
                        index={index}
                        isDragDisabled={isReordering}
                      >
                        {(dragProvided, snapshot) => (
                          <TableRow
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            data-testid={`custom-field-sections-row-${section.id}`}
                            sx={snapshot.isDragging ? styles.draggingRow : {}}
                          >
                            <TableCell sx={styles.orderCell}>
                              <Box sx={styles.orderCellContent}>
                                <Box
                                  component="span"
                                  {...dragProvided.dragHandleProps}
                                  sx={styles.dragHandle}
                                  data-testid={`custom-field-sections-drag-handle-${section.id}`}
                                >
                                  <i className="uil uil-draggabledots" />
                                </Box>
                                <span>{index + 1}.</span>
                              </Box>
                            </TableCell>
                            <TableCell sx={styles.labelCell}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                {section.name}
                                {section.isDefault && (
                                  <Chip
                                    variant="outlined"
                                    label="Default"
                                    size="small"
                                    sx={styles.typeChip}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={styles.sectionCell}>
                              {section.fieldCount}
                            </TableCell>
                            <TableCell sx={styles.dateCell}>
                              {formatDate(section.updatedAt)}
                            </TableCell>
                            <TableCell sx={styles.actionsCell}>
                              <IconButton
                                aria-label="Edit section"
                                data-testid={`custom-field-sections-edit-${section.id}`}
                                onClick={() => handleEdit(section)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                aria-label="Delete section"
                                data-testid={`custom-field-sections-delete-${section.id}`}
                                onClick={() => handleDelete(section)}
                                disabled={section.isDefault || isDeleting}
                              >
                                <TrashIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
        {!isLoading && orderedSections.length === 0 && (
          <Box
            sx={styles.emptyWrapper}
            data-testid="custom-field-sections-empty-state"
          >
            <EmptyState
              title="No sections yet"
              description="Create a section to group related fields on the case page."
            />
          </Box>
        )}
      </TableContainer>

      <CreateCustomFieldSectionSideSheet
        isOpen={createSheetOpen || localCreateSheetOpen}
        onClose={handleCloseCreateSheet}
        onSuccess={handleCloseCreateSheet}
        mode="create"
      />

      <CreateCustomFieldSectionSideSheet
        isOpen={editSheetOpen}
        onClose={() => {
          setEditSheetOpen(false);
          setSectionToEdit(undefined);
        }}
        onSuccess={() => {
          setEditSheetOpen(false);
          setSectionToEdit(undefined);
        }}
        mode="edit"
        sectionToEdit={sectionToEdit}
        setSectionToDelete={setSectionToDelete}
      />

      <ConfirmationBeforeRemoveModal
        open={Boolean(sectionToDelete)}
        title={`Delete section "${sectionToDelete?.name ?? ''}"?`}
        content={`Deleting a section moves its fields to "${defaultSection?.name ?? 'ungrouped'}" — no field or stored value is deleted.`}
        confirmButtonText="Delete section"
        onConfirm={confirmDelete}
        onCancel={() => setSectionToDelete(undefined)}
        onClose={() => setSectionToDelete(undefined)}
      />
    </>
  );
};
